import { normalizeForSearch } from './search-normalize';
import type { ScriptureTopic } from '@/types/scripture';

/**
 * The minimal topic shape searchTopics reads: slug + name + description.
 * The offline Bible-search bundle (public/bible-search/topics.json) carries
 * exactly this plus displayOrder + verses, with no Supabase `id`, so the
 * matcher must not require `id`. searchTopics is generic over this record so
 * a ScriptureTopic[] caller still gets ScriptureTopic[] back (keeping `.id`
 * and every other field) while the offline loader passes a slug-only record
 * and gets that type back.
 */
export type TopicSearchRecord = Pick<ScriptureTopic, 'slug' | 'name' | 'description'>;

// Match-strength band for a topic result (08.4). Drives the Top-preview
// Verses↔Topics ordering: 'whole' (the query IS the topic's name or a synonym
// term) beats even a contiguous verse phrase; 'synonym' (query contains a
// synonym term) leads verses only when the verse side is not a phrase. 'direct'
// (query tokens scattered in name + description) is a RECALL/display band only
// and never leads — it is too broad for common Bible words ("god"/"love" hit a
// topic name, "faith"/"jesus" hit a description). 'partial'/'fuzzy' never lead.
// 'none' is the empty-query passthrough only (browse-all, never active search).
export type TopicMatchStrength =
  | 'whole'
  | 'direct'
  | 'synonym'
  | 'partial'
  | 'fuzzy'
  | 'none';

/**
 * Smart topic search for the share verse picker. Pure + self-contained
 * (own Levenshtein) so it's unit-testable and carries no React/Supabase
 * deps. Three passes, deterministic order: direct > synonym > fuzzy, each
 * stably sorted by topic name.
 *
 * English-only by design — the curated taxonomy is en-US only (rule #7).
 * When es/pt taxonomy ships, the synonym map gets native localizations
 * (not machine translations), same posture as the taxonomy itself.
 */

// Curated lay-term → topic-slug synonyms, so a user typing a feeling or
// situation ("money", "passed away", "where is god") surfaces the right
// topic even when the word isn't in the title. Terms may appear under
// multiple slugs where genuinely ambiguous (e.g. "temptation").
const TOPIC_SYNONYMS: Record<string, string[]> = {
  'anxiety-fear': ['worry', 'worried', 'worrying', 'anxious', 'anxiety', 'fear', 'fearful', 'afraid', 'scared', 'panic', 'panicked', 'panic attacks', 'stress', 'stressed', 'stressful', 'nervous', 'overwhelmed', 'overwhelming', 'dread'],
  healing: ['sick', 'illness', 'disease', 'cancer', 'surgery', 'recovery', 'heal', 'pain', 'hospital', 'diagnosis', 'health'],
  'family-relationships': ['family', 'relatives', 'parents', 'siblings', 'brother', 'sister', 'mom', 'dad', 'in-laws'],
  parenting: ['kids', 'child', 'children', 'son', 'daughter', 'baby', 'raising', 'teenager', 'motherhood', 'fatherhood'],
  provision: ['money', 'finances', 'financial', 'bills', 'rent', 'debt', 'job loss', 'broke', 'provide', 'need', 'poverty', 'paycheck'],
  'grief-lament': ['grief', 'grieving', 'loss', 'death', 'died', 'passed away', 'mourning', 'sorrow', 'lament', 'funeral', 'heartbroken', 'sad'],
  'hope-trust': ['hope', 'hopeless', 'trust', 'waiting', 'uncertainty', 'future'],
  marriage: ['marriage', 'spouse', 'husband', 'wife', 'divorce', 'separation', 'wedding'],
  'work-career': ['work', 'job', 'career', 'boss', 'coworker', 'unemployment', 'business', 'calling', 'layoff'],
  'guidance-wisdom': ['guidance', 'decision', 'direction', 'wisdom', 'choice', 'discernment', 'confused', 'crossroads'],
  'praise-thanksgiving': ['praise', 'thankful', 'thanks', 'grateful', 'gratitude', 'worship', 'blessed'],
  'gods-faithfulness': ['faithfulness', 'faithful', 'promises', 'dependable', 'never fails'],
  identity: ['identity', 'worth', 'value', 'who am i', 'self-worth', 'insecure', 'enough', 'belonging'],
  intercession: ['intercede', 'intercession', 'praying for others', 'pray for', 'on behalf'],
  'salvation-loved-ones': ['salvation', 'saved', 'unbeliever', 'unsaved', 'lost', 'loved one', 'conversion'],
  forgiveness: ['forgive', 'forgiveness', 'grudge', 'let go', 'pardon', 'reconcile'],
  'anger-bitterness': ['anger', 'angry', 'mad', 'rage', 'bitter', 'bitterness', 'resentment', 'hate', 'frustrated'],
  loneliness: ['lonely', 'alone', 'isolated', 'isolation', 'abandoned', 'no one'],
  'struggle-with-sin': ['sin', 'struggle', 'temptation', 'tempted', 'habit', 'addiction', 'relapse'],
  'sexual-temptation-purity': ['lust', 'purity', 'porn', 'pornography', 'sexual', 'temptation', 'chastity'],
  repentance: ['repent', 'repentance', 'sorry', 'guilt', 'conviction', 'confession', 'turn back'],
  'mental-health': ['depression', 'depressed', 'mental health', 'suicidal', 'despair', 'therapy', 'panic attacks'],
  'pride-humility': ['pride', 'proud', 'humble', 'humility', 'ego', 'arrogance', 'boast'],
  'doubt-unbelief': ['doubt', 'unbelief', 'questioning', 'skeptic', 'where is god', 'hard to believe'],
  'spiritual-dryness': ['dry', 'dryness', 'distant', 'far from god', 'empty', 'numb', 'wilderness'],
  protection: ['protect', 'protection', 'safety', 'safe', 'danger', 'travel', 'guard', 'shield', 'harm'],
};

export const MAX_REMEMBER_SYNONYM_TERMS = 48;

// This taxonomy is static for the lifetime of the module. Normalize it once,
// not on every Remember keystroke.
const NORMALIZED_TOPIC_SYNONYMS = Object.values(TOPIC_SYNONYMS).map((terms) =>
  terms.map((term) => normalizeForSearch(term)),
);

/**
 * Expand a free-text query into the curated lay-term synonyms it implies, for
 * the Remember feeling-search (remember-lenses-search arc, Phase 2). Returns a
 * DE-DUPED list of NORMALIZED terms: the literal normalized query PLUS every
 * synonym term of each slug whose term-set the query matches.
 *
 * Scans ONLY `TOPIC_SYNONYMS` (never topic name/description; does NOT route
 * through searchTopicsWithStrength, which also matches name+description and
 * would over-expand). The slug-match test is the SAME normalized
 * whole / contains / partial logic the synonym pass uses (the `synReason`
 * branch above): a term equal to the query, the query containing a term
 * (multi-word terms like "passed away"), or a 4+ char query a term contains.
 *
 * English-only by design (rule #7 carve-out class): the curated synonym map is
 * en-US only, so Remember's caller only invokes this for `en` and passes `[]`
 * for es/pt (literal-keyword fallback). The raw `TOPIC_SYNONYMS` const stays
 * PRIVATE to this module; this is the deliberate exported seam.
 */
export function expandQueryToSynonymTerms(query: string): string[] {
  const nq = normalizeForSearch(query);
  if (!nq) return [];
  const out = new Set<string>([nq]);
  const isComposite = nq.includes(' ');
  for (const terms of NORMALIZED_TOPIC_SYNONYMS) {
    // A composite query only expands when the complete phrase is curated.
    // This prevents "afraid money" from turning strict AND into a broad OR.
    const slugMatched = terms.some((term) =>
      isComposite ? nq === term : nq === term || (nq.length >= 4 && term.includes(nq)),
    );
    if (slugMatched) {
      for (const term of terms) {
        out.add(term);
        if (out.size >= MAX_REMEMBER_SYNONYM_TERMS) return [...out];
      }
    }
  }
  return [...out];
}

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  let curr = new Array<number>(b.length + 1);
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[b.length];
}

/**
 * Returns topics matching the query, ordered direct > synonym > fuzzy,
 * stable by name within each band. Empty query → all topics A→Z. When
 * nothing reasonably matches (gibberish), returns [] so the caller can
 * show a "no matches" state rather than a confidently-wrong topic.
 */
export function searchTopics<T extends TopicSearchRecord>(query: string, topics: T[]): T[] {
  // Thin wrapper: identical order + dedup for every query (including '') as
  // before 08.4 — strength is an ADDED label, not a behavior change. The
  // ShareVersePicker caller contract is byte-for-byte preserved.
  return searchTopicsWithStrength(query, topics).map((x) => x.topic);
}

// Strongest-reason rank within the synonym pass (lower = stronger).
const SYN_REASON_RANK: Record<'whole' | 'synonym' | 'partial', number> = {
  whole: 0,
  synonym: 1,
  partial: 2,
};

/**
 * Same matching + order + dedup as {@link searchTopics}, but each result is
 * tagged with its {@link TopicMatchStrength} band (08.4). The included set and
 * order are IDENTICAL to searchTopics; strength is only a label derived per
 * result from `nq` vs the topic's name + synonym terms. Precedence
 * whole > direct > synonym > partial > fuzzy. Empty query → all topics A→Z,
 * each tagged 'none' (the browse-all passthrough).
 */
export function searchTopicsWithStrength<T extends TopicSearchRecord>(
  query: string,
  topics: T[],
): Array<{ topic: T; strength: TopicMatchStrength }> {
  const byName = [...topics].sort((a, b) => a.name.localeCompare(b.name));
  const nq = normalizeForSearch(query);
  if (!nq) return byName.map((topic) => ({ topic, strength: 'none' as const }));

  const tokens = nq.split(' ');

  // Pass 1 — direct: name + description contains every query token.
  const direct = byName.filter((t) => {
    const hay = normalizeForSearch(`${t.name} ${t.description}`);
    return tokens.every((tok) => hay.includes(tok));
  });
  // Dedup on slug (unique within a locale), not id, so the matcher needs no
  // Supabase id and stays usable for the offline bundle's slug-only records.
  const directSlugs = new Set(direct.map((t) => t.slug));

  // Pass 2 — synonym (phrase-aware), recording the STRONGEST reason per slug so
  // the result can be banded: a term equal to the query is 'whole' (the user
  // typed the feeling/topic itself); the query CONTAINING a term is 'synonym'
  // (handles multi-word terms like "passed away"); a 4+ char query that a term
  // contains is 'partial' (partial typing).
  const synReason = new Map<string, 'whole' | 'synonym' | 'partial'>();
  for (const [slug, terms] of Object.entries(TOPIC_SYNONYMS)) {
    for (const term of terms) {
      const nt = normalizeForSearch(term);
      let reason: 'whole' | 'synonym' | 'partial' | null = null;
      if (nq === nt) reason = 'whole';
      else if (nq.includes(nt)) reason = 'synonym';
      else if (nq.length >= 4 && nt.includes(nq)) reason = 'partial';
      if (reason) {
        const prev = synReason.get(slug);
        if (prev === undefined || SYN_REASON_RANK[reason] < SYN_REASON_RANK[prev]) {
          synReason.set(slug, reason);
        }
      }
    }
  }
  const synonym = byName.filter((t) => synReason.has(t.slug) && !directSlugs.has(t.slug));

  const combined = [...direct, ...synonym];
  if (combined.length > 0) {
    return combined.map((topic) => {
      const nameWhole = nq === normalizeForSearch(topic.name);
      const synR = synReason.get(topic.slug);
      let strength: TopicMatchStrength;
      if (nameWhole || synR === 'whole') strength = 'whole';
      else if (directSlugs.has(topic.slug)) strength = 'direct';
      else if (synR === 'synonym') strength = 'synonym';
      else strength = 'partial'; // synonym-band, not direct/whole → must be partial
      return { topic, strength };
    });
  }

  // Pass 3 — fuzzy fallback: only matches within a length-scaled distance
  // threshold, capped, so typos surface ("greif" → Grief) but gibberish
  // returns nothing. Distance is the min over the topic's name words and
  // its synonym terms.
  const threshold = nq.length <= 4 ? 1 : nq.length <= 7 ? 2 : 3;
  const scored = byName
    .map((t) => {
      const candidates = [
        normalizeForSearch(t.name),
        ...normalizeForSearch(t.name).split(' '),
        ...(TOPIC_SYNONYMS[t.slug] ?? []).flatMap((term) => {
          const nt = normalizeForSearch(term);
          return [nt, ...nt.split(' ')];
        }),
      ];
      let best = Infinity;
      for (const c of candidates) {
        const d = levenshtein(nq, c);
        if (d < best) best = d;
      }
      return { topic: t, distance: best };
    })
    .filter((x) => x.distance <= threshold)
    .sort((a, b) => a.distance - b.distance || a.topic.name.localeCompare(b.topic.name))
    .slice(0, 6);

  return scored.map((x) => ({ topic: x.topic, strength: 'fuzzy' as const }));
}
